'use client';

import { StudioAction } from '@/components/ui/StudioAction';
import { CreationGlassBackdrop } from '@/components/visual-editor/CreationGlassBackdrop';

import styles from '@/components/subscription/Commerce.module.css';
import { SystemLoadingIcon as Loader2 } from '@/components/ui/SystemLoading';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { CircleDollarSign, Gift, Link2, X } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useCreditsStore } from '@/lib/stores/credits-store';
import { useCreditsJourneyStore } from '@/lib/stores/credits-journey-store';
import { creatorReferralsModal } from '@/lib/stores/creator-referrals-modal-store';
import { useAuthStore } from '@/stores/authStore';
import { CREDITS_WELCOME_TIMEOUT_MS } from '@/lib/credits/journey';
import { CreditsPolicyNote } from './CreditsPolicyNote';
import { useCreditsJourneyAccount } from './useCreditsJourneyAccount';

export function CreditsWelcomePopover({ owner, anchorRef, autoShow, blocked }: {
  owner: string;
  anchorRef: RefObject<HTMLButtonElement | null>;
  autoShow: boolean;
  blocked: boolean;
}) {
  useCreditsJourneyAccount();
  const t = useTranslations('creditsJourney');
  const locale = useLocale();
  const userId = useAuthStore((state) => state.user?.id);
  const credits = useCreditsStore((state) => state.credits);
  const creditsLoading = useCreditsStore((state) => state.loading);
  const creditsError = useCreditsStore((state) => state.error);
  const ready = useCreditsJourneyStore((state) => state.ready);
  const accountId = useCreditsJourneyStore((state) => state.userId);
  const shown = useCreditsJourneyStore((state) => state.welcomeShown);
  const read = useCreditsJourneyStore((state) => state.welcomeRead);
  const visible = useCreditsJourneyStore((state) => state.welcomeOwner === owner);
  const automatic = useCreditsJourneyStore((state) => state.welcomeAutomatic);
  const saving = useCreditsJourneyStore((state) => state.welcomeSaving > 0);
  const saveError = useCreditsJourneyStore((state) => state.welcomeSaveError);
  const panelRef = useRef<HTMLDivElement>(null);
  const remainingMs = useRef(CREDITS_WELCOME_TIMEOUT_MS);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [documentHidden, setDocumentHidden] = useState(false);
  const [position, setPosition] = useState({ left: 12, top: 72, maxHeight: 520 });
  const open = visible && accountId === userId && Boolean(credits);

  useEffect(() => {
    if (credits?.welcome_credit_status === 'pending' || credits?.welcome_credit_status === 'granted') return;
    if (!autoShow || blocked || !ready || accountId !== userId || shown || read
      || !credits || creditsLoading || creditsError) return;
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect || !rect.width || !rect.height) return;
    void useCreditsJourneyStore.getState().openWelcome(owner, true);
  }, [autoShow, blocked, ready, accountId, userId, shown, read, credits, creditsLoading, creditsError, owner, anchorRef]);

  useEffect(() => {
    if (!open) return;
    remainingMs.current = CREDITS_WELCOME_TIMEOUT_MS;
    setHovered(false);
    setFocused(false);
    const handleVisibility = () => setDocumentHidden(document.visibilityState === 'hidden');
    handleVisibility();
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [open]);

  useEffect(() => {
    if (!open || !automatic || hovered || focused || documentHidden || saving || saveError) return;
    const startedAt = performance.now();
    const timer = window.setTimeout(() => useCreditsJourneyStore.getState().hideWelcome(), remainingMs.current);
    return () => {
      window.clearTimeout(timer);
      remainingMs.current = Math.max(0, remainingMs.current - (performance.now() - startedAt));
    };
  }, [open, automatic, hovered, focused, documentHidden, saving, saveError]);

  useLayoutEffect(() => {
    if (!open) return;
    const reposition = () => {
      const anchor = anchorRef.current?.getBoundingClientRect();
      if (!anchor) return;
      const width = panelRef.current?.offsetWidth ?? Math.min(384, window.innerWidth - 24);
      const maxHeight = window.innerHeight - 24;
      const height = Math.min(panelRef.current?.offsetHeight ?? 320, maxHeight);
      const below = anchor.bottom + 10;
      setPosition({
        left: Math.max(12, Math.min(anchor.right - width, window.innerWidth - width - 12)),
        top: Math.max(12, below + height <= window.innerHeight - 12 ? below : anchor.top - height - 10),
        maxHeight,
      });
    };
    reposition();
    const observer = new ResizeObserver(reposition);
    if (panelRef.current) observer.observe(panelRef.current);
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open, anchorRef]);

  const closeAsRead = useCallback(async () => {
    if (useCreditsJourneyStore.getState().welcomeSaving > 0) return;
    const closed = await useCreditsJourneyStore.getState().closeWelcomeAsRead();
    if (closed) anchorRef.current?.focus({ preventScroll: true });
  }, [anchorRef]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      void closeAsRead();
    };
    const handlePointerDown = (event: PointerEvent) => {
      if (panelRef.current?.contains(event.target as Node) || anchorRef.current?.contains(event.target as Node)) return;
      useCreditsJourneyStore.getState().hideWelcome();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [open, closeAsRead, anchorRef]);

  if (!open || !credits) return null;
  return createPortal(
    <div ref={panelRef} data-credits-welcome role="dialog" aria-modal="false" aria-labelledby={`${owner}-credits-title`}
      style={position}
      onMouseEnter={(event) => { event.stopPropagation(); setHovered(true); }}
      onMouseLeave={() => setHovered(false)}
      onFocus={(event) => { event.stopPropagation(); setFocused(true); }}
      onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocused(false); }}
      className={`${styles.surface} ${styles.creditPopover} fixed z-[240] w-[min(24rem,calc(100vw-1.5rem))] overflow-y-auto rounded-2xl border p-6`}>
      <CreationGlassBackdrop />
      <button type="button" disabled={saving} onClick={closeAsRead} aria-label={t('closeWelcome')}
        className="absolute right-2 top-2 flex h-11 w-11 items-center justify-center rounded-full text-[#64748B] transition-colors hover:bg-[#F1F5F9] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDE9] disabled:opacity-50">
        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <X className="h-3.5 w-3.5" aria-hidden />}
      </button>
      <div className="flex items-center gap-3 pr-7">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#4ABDE9]/12 text-[#2388B0]">
          <CircleDollarSign className="h-6 w-6" aria-hidden />
        </span>
        <div>
          <h2 id={`${owner}-credits-title`} className="text-[16px] font-semibold leading-6 text-[#19232E]">
            {credits.free_credit_policy === 'welcome_once' && credits.welcome_credit_status === 'granted'
              ? t('oneTimeTitle', { count: (credits.welcome_credit_amount ?? 0).toLocaleString(locale) })
              : t('welcomeTitle')}
          </h2>
          <p className="mt-0.5 text-[12px] leading-5 text-[#64748B]">{t('welcomeDescription')}</p>
        </div>
      </div>
      <CreditsPolicyNote className="mt-4 rounded-xl bg-[#F7FAFC] px-3 py-2.5 text-[12px]" />
      <div className="mt-4 flex items-start gap-2">
        <Gift className="mt-0.5 h-4 w-4 shrink-0 text-[#2388B0]" aria-hidden />
        <div>
          <h3 className="text-[12px] font-semibold text-[#334155]">{t('moreTitle')}</h3>
          <p className="mt-1 text-[12px] leading-5 text-[#64748B]">{t('moreDescription')}</p>
        </div>
      </div>
      <StudioAction variant="secondary" size="compact" type="button" onClick={() => { useCreditsJourneyStore.getState().hideWelcome(); creatorReferralsModal.open('redeem'); }}
        className="mt-4 w-full gap-2">
        <Link2 className="h-4 w-4 shrink-0" aria-hidden />{t('redeem')}
      </StudioAction>
      {saveError && <p className="mt-2 text-[11px] leading-4 text-[#8A5B13]" role="status">{t('welcomeSaveFailed')}</p>}
    </div>, document.body,
  );
}
